import { type BrowserOptions, reactRouterBrowserTracingIntegration } from '@sentry/react'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'
import { fetchNetworkFailureMessages, isDynamicImportError } from 'utils'

import { sentryDsn } from './environment'

/**
 * Browser page translation replaces the text nodes React rendered, so React's next removeChild or
 * insertBefore throws a NotFoundError that the boundary recovers from. Chrome marks a translated
 * document with one of these classes; Edge's translator leaves the document element alone and
 * instead marks each element it rewrote with a `_msttexthash` attribute. Those errors are dropped;
 * a NotFoundError on an untranslated page is ours and is reported with the tag so it stands out.
 */
const translatedPageClasses = ['translated-ltr', 'translated-rtl']

const isPageTranslated = () =>
  translatedPageClasses.some((className) =>
    document.documentElement.classList.contains(className)
  ) || document.querySelector('[_msttexthash]') !== null

const isDomNotFoundError = (error: unknown) =>
  error instanceof Error && error.name === 'NotFoundError'

const identifyingKeyFragments = { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] }

export const sentryConfig: BrowserOptions = {
  dsn: sentryDsn,
  release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
  // Matches the defaults @sentry/react 11.10.0 applies when dataCollection is unset, except that
  // cookies are off rather than filtered by a deny-list.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: identifyingKeyFragments, response: identifyingKeyFragments },
    httpBodies: [],
    urlQueryParams: identifyingKeyFragments
  },
  integrations: [
    reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  environment: window.location.host,
  enabled: window.location.host !== 'localhost:1234',
  ignoreErrors: [
    'API response not ok: 401',
    ...fetchNetworkFailureMessages,
    // amazon-cognito-identity-js and Amplify report a dropped connection and a sign-out that
    // never completed with these fixed strings.
    'Network error',
    'Signout timeout fail'
  ],
  beforeSend: (event, hint) => {
    const error = hint.originalException
    if (isDynamicImportError(error)) return null
    if (isDomNotFoundError(error)) {
      if (isPageTranslated()) return null
      event.tags = { ...event.tags, translated: false }
    }
    return event
  }
}
